import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { DndContext, DragOverlay, pointerWithin } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import cn from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { CustomStylesCtx, useTextStyle } from '@webapp/common/components/custom-styles';
import { useSensors } from '@webapp/common/components/sortable';
import { CssPageBlock, CssQuestionType } from '@webapp/common/components/survey-custom';
import { AnswerType, QuestionType } from '@webapp/common/resources/survey';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import { selectInfo } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import { ListSelectResults } from '../test-text';

import { Item, SortableItem } from './item';

import css from './rank.css';

const modifiers = [restrictToVerticalAxis, restrictToParentElement];

const itemStyle = {
    background: 'none',
};

export const Rank: VFC<SurveyQuestion> = ({
    question: { id: questionId, answersActors, currentPoints, factTime, maxPoints, targetTime, type },
    readOnly,
    twisterReadOnly,
}) => {
    const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
    const [fixedRef, setFixedRef] = useState<HTMLDivElement | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const [domRect, setDomRect] = useState<DOMRect | null>(null);
    const {
        params: {
            // main: { completePage },
            tests: { scoreLabel },
        },
    } = useStateSelector(selectInfo);
    const [, updateRerenderHack] = useState(1);

    useEffect(() => {
        const sub = () => updateRerenderHack((prev) => (prev + 1) % 10);
        const unsubs = answersActors.map((actor) => {
            return actor.subscribe(sub);
        });
        return () => {
            unsubs.forEach((unsub) => unsub.unsubscribe());
        };
    }, []);

    const rawAnswers = answersActors.filter(
        (a) => a.getSnapshot().context.hidden !== true && a.getSnapshot().context.hiddenByAnswersHide !== true,
    );
    const answers = rawAnswers
        .slice()
        .sort(
            (prevAnswer, nextAnswer) =>
                Number(prevAnswer.getSnapshot().context.response.getSnapshot().context.value) -
                Number(nextAnswer.getSnapshot().context.response.getSnapshot().context.value),
        );
    const items = answers.map((ans) => String(ans.getSnapshot().context.id));
    const { text: textStyle } = useContext(CustomStylesCtx);
    const sensors = useSensors();
    const [draggedItem, setDraggedItem] = useState<{ item: QuestionAnswerActor } & { idx: number }>(null);
    const { answerStyle, listStyle } = useTextStyle();
    const [activeElWidth, setActiveElWidth] = useState<undefined | number>(undefined);

    const handlerIconStyle = useMemo(
        () => ({
            color: answerStyle.color,
        }),
        [answerStyle.color],
    );

    const handleChangeSort = useCallback(
        ({ active, over }: DragEndEvent) => {
            setDraggedItem(null);

            const oldIndex = answers.findIndex((a) => a.getSnapshot().context.id === Number(active.id));
            // TODO check, over maybe null
            const newIndex = over ? answers.findIndex((a) => a.getSnapshot().context.id === Number(over.id)) : oldIndex;
            const answersIds = answers.map((a) => a.getSnapshot().context.id);
            const newAnswers = arrayMove(answersIds, oldIndex, newIndex);

            newAnswers.forEach((id, idx) => {
                const answer = rawAnswers.find((a) => a.getSnapshot().context.id === id);
                if (answer && answer.getSnapshot().context.response) {
                    answer.getSnapshot().context.response.send({ type: 'CHANGE', value: idx, questionId });
                }
            });
        },
        [answers, rawAnswers],
    );

    const onDragStart = useCallback(
        ({ active }: DragStartEvent) => {
            const id = Number(active.id);
            const idx = answers.findIndex((a) => a.getSnapshot().context.id === id);
            const dragged = answers[idx];
            const el = document.querySelector(`.rank-sortable-item-${idx}`);
            if (el) {
                setActiveElWidth(el.clientWidth + 20);
            }

            setDraggedItem({ item: dragged, idx: idx + 1 });
        },
        [answers],
    );
    const showResults =
        type === QuestionType.TEST_RANK && (factTime || targetTime || maxPoints || currentPoints) && readOnly;

    const showTimer = Boolean(factTime || targetTime);
    const questions = useStateSelector((s) => s.questions);
    const renderDraggedItem = () => {
        if (!draggedItem) {
            return null;
        }
        let value = draggedItem.item.getSnapshot().context.value;
        let forwardParentAnswer: QuestionAnswerActor | null = null;
        questions.forEach((q) => {
            const founded = q.getSnapshot().context.answersActors.find((a) => {
                return a.getSnapshot().context.id === draggedItem.item.getSnapshot().context.forwardParent;
            });
            if (founded) {
                forwardParentAnswer = founded;
            }
        });
        if (forwardParentAnswer && forwardParentAnswer.getSnapshot().context.type === AnswerType.SELF) {
            const sub = () => updateRerenderHack((prev) => (prev + 1) % 10);
            forwardParentAnswer.subscribe(sub);
            value = [forwardParentAnswer.getSnapshot().context.response.getSnapshot().context.extra?.toString() || ''];
        }
        return (
            <Item
                className={cn(CssPageBlock.ANSWER, css.draggable)}
                iconStyle={handlerIconStyle}
                idx={draggedItem.idx}
                itemStyle={{ ...itemStyle, minWidth: activeElWidth }}
                textStyle={textStyle}
                value={value}
            />
        );
    };
    const content = () => {
        return (
            <DndContext
                collisionDetection={pointerWithin}
                modifiers={modifiers}
                sensors={sensors}
                onDragEnd={handleChangeSort}
                onDragStart={onDragStart}
            >
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    <div style={listStyle} className={cn(CssQuestionType.QUESTION, CssQuestionType.RANK, css.rank)}>
                        {answers.map((answer, idx) => {
                            const {
                                id,
                                value: valueRaw,
                                testCheckStatus,
                                forwardParent,
                            } = answer.getSnapshot().context;
                            let value = valueRaw;
                            let forwardParentAnswer: QuestionAnswerActor | null = null;
                            questions.forEach((q) => {
                                const founded = q.getSnapshot().context.answersActors.find((a) => {
                                    return a.getSnapshot().context.id === forwardParent;
                                });
                                if (founded) {
                                    forwardParentAnswer = founded;
                                }
                            });
                            if (
                                forwardParentAnswer &&
                                forwardParentAnswer.getSnapshot().context.type === AnswerType.SELF
                            ) {
                                const sub = () => updateRerenderHack((prev) => (prev + 1) % 10);
                                forwardParentAnswer.subscribe(sub);
                                value = [
                                    forwardParentAnswer
                                        .getSnapshot()
                                        .context.response.getSnapshot()
                                        .context.extra?.toString() || '',
                                ];
                            }
                            return (
                                <SortableItem
                                    iconStyle={handlerIconStyle}
                                    id={id}
                                    idx={idx + 1}
                                    itemStyle={itemStyle}
                                    key={id}
                                    showAnswerIcon={Boolean(showResults)}
                                    testCheckStatus={testCheckStatus}
                                    textStyle={textStyle}
                                    value={value}
                                    className={cn(CssPageBlock.ANSWER, css.sortableItem, `rank-sortable-item-${idx}`, {
                                        [css.current]: draggedItem && draggedItem.item.getSnapshot().context.id === id,
                                    })}
                                />
                            );
                        })}
                    </div>
                </SortableContext>
                <DragOverlay>{renderDraggedItem()}</DragOverlay>
            </DndContext>
        );
    };

    useEffect(() => {
        if (rootRef) {
            const recompute = () => {
                setDomRect(rootRef.getBoundingClientRect());
                animationFrameRef.current = window.requestAnimationFrame(recompute);
            };
            const ro = new ResizeObserver(recompute);
            ro.observe(rootRef);
            animationFrameRef.current = window.requestAnimationFrame(recompute);

            return () => {
                ro.unobserve(rootRef);
                window.cancelAnimationFrame(animationFrameRef.current);
            };
        }
    }, [rootRef]);

    useEffect(() => {
        if (fixedRef) {
            let startY = 0;
            const closestBundle = rootRef.closest('.currentBundleNew');
            const touchStartHandler = function (e: TouchEvent) {
                if ((e.target as HTMLElement).closest('.handler')) {
                    return;
                }
                const touchObj = e.changedTouches[0];
                startY = parseInt(touchObj.clientY.toString());
                e.preventDefault();
            };
            const touchMoveHandler = function (e: TouchEvent) {
                if ((e.target as HTMLElement).closest('.handler')) {
                    return;
                }
                const touchObj = e.changedTouches[0],
                    dist = parseInt(touchObj.clientY.toString()) - startY;

                closestBundle?.scrollBy(0, -dist);
                startY = parseInt(touchObj.clientY.toString());
                e.preventDefault();
            };
            fixedRef.addEventListener('touchstart', touchStartHandler, false);

            fixedRef.addEventListener('touchmove', touchMoveHandler, false);

            return () => {
                fixedRef.removeEventListener('touchstart', touchStartHandler, false);

                fixedRef.removeEventListener('touchmove', touchMoveHandler, false);
            };
        }
    }, [fixedRef]);

    return (
        <div className={cn(css.root)}>
            <div className={css.content} ref={setRootRef}>
                {content()}
            </div>
            {!twisterReadOnly &&
                createPortal(
                    <div
                        className={css.fixed}
                        ref={setFixedRef}
                        style={
                            domRect
                                ? {
                                      top: domRect.top,
                                      left: domRect.left,
                                      height: domRect.height + 5,
                                      width: domRect.width - 10,
                                  }
                                : undefined
                        }
                    >
                        {content()}
                    </div>,
                    document.body,
                )}
            {showResults && (
                <ListSelectResults
                    answers={answers}
                    currentPoints={currentPoints}
                    factTime={factTime}
                    maxPoints={maxPoints}
                    showTimer={showTimer}
                    targetTime={targetTime}
                    pointsLabel={scoreLabel}
                />
            )}
        </div>
    );
};
Rank.displayName = 'Rank';
